import { isApiError } from "@sagot/api-client";
import { consoleRoutes, WORKFLOW_ROLLBACK_BLOCKED_REASON, type WorkflowTrigger } from "@sagot/constants";
import { useTranslation } from "@sagot/i18n";
import { AppLink, Badge, Button, EmptyState, ErrorState, Icon, Skeleton, StatusBadge, toast } from "@sagot/ui";
import { useState } from "react";
import { useErrorMessage } from "../../../../lib/api-errors";
import { useWorkspaceContext } from "../../../../session/workspace-context";
import { useFormatters } from "../../shared/format";
import { DIFF_DRAFT, DIFF_LIVE, type VersionItem } from "../p4-types";
import type { WorkflowDetail } from "../types";
import { useRollback, useVersions } from "../use-workflows-p4";
import { RollbackDialog } from "./rollback-dialog";
import { VersionCompare } from "./version-compare";
import { VersionSheet } from "./version-sheet";

const V = "engage.workflows.versions";

interface VersionsPanelProps {
  workflow: WorkflowDetail;
  trigger: WorkflowTrigger;
  /** The builder holds edits that are not saved yet. */
  dirty: boolean;
  canManage: boolean;
  /** The draft was replaced by an older version: the builder shows it. */
  onRolledBack: (workflow: WorkflowDetail, number: number) => void;
}

interface RowProps {
  version: VersionItem;
  canManage: boolean;
  onView: () => void;
  onCompare: () => void;
  onRollback: () => void;
}

function VersionRow({ version, canManage, onView, onCompare, onRollback }: RowProps) {
  const { t } = useTranslation();
  const format = useFormatters();
  return (
    <li className="flex flex-col gap-2 p-4 sm:flex-row sm:items-center sm:justify-between" data-testid="version-row">
      <div>
        <p className="flex items-center gap-2 font-semibold">
          <span className="font-mono">{t(`${V}.versionShort`, { number: version.number })}</span>
          {version.live ? <StatusBadge status="success" label={t(`${V}.live`)} /> : null}
        </p>
        <p className="text-xs text-fg-subtle">{t(`${V}.rowMeta`, { name: version.published_by_name ?? "—", when: format.dateTime(version.published_at), runs: version.runs })}</p>
      </div>
      <div className="flex flex-wrap gap-2">
        <Button variant="ghost" size="sm" aria-label={t(`${V}.viewVersion`, { number: version.number })} onClick={onView}>{t(`${V}.view`)}</Button>
        {version.live ? null : (
          <>
            <Button variant="ghost" size="sm" aria-label={t(`${V}.compareVersion`, { number: version.number })} onClick={onCompare}>{t(`${V}.compareLive`)}</Button>
            {canManage ? (
              <Button variant="secondary" size="sm" aria-label={t(`${V}.rollbackVersion`, { number: version.number })} onClick={onRollback}>
                <Icon name="rotate-ccw" />
                {t(`${V}.rollback`)}
              </Button>
            ) : null}
          </>
        )}
      </div>
    </li>
  );
}

/** Every publish saved as a version: open one read-only, compare two side by side, or roll back (copies it into the draft; visitors see nothing until it is published). */
export function VersionsPanel({ workflow, trigger, dirty, canManage, onRolledBack }: VersionsPanelProps) {
  const { t } = useTranslation();
  const format = useFormatters();
  const message = useErrorMessage();
  const { summary } = useWorkspaceContext();
  const versions = useVersions(workflow.id, true);
  const rollbackMutation = useRollback(workflow.id);
  const [viewing, setViewing] = useState<number | null>(null);
  const [comparing, setComparing] = useState<{ base: string; target: string } | null>(null);
  const [confirming, setConfirming] = useState<number | null>(null);

  const rollback = (number: number, force: boolean) => {
    rollbackMutation.mutate({ number, force }, {
      onSuccess: (detail) => {
        setConfirming(null);
        toast.success(t(`${V}.rolledBack`, { number }));
        onRolledBack(detail, number);
      },
      onError: (error) => {
        const blocked = isApiError(error) && error.status === 409 && error.details.reason === WORKFLOW_ROLLBACK_BLOCKED_REASON;
        if (blocked && !force) { setConfirming(number); return; }
        setConfirming(null);
        toast.error(message(error));
      },
    });
  };
  const requestRollback = (number: number) => { if (dirty) setConfirming(number); else rollback(number, false); };

  if (versions.isError) return <ErrorState title={t(`${V}.loadFailed`)} onRetry={() => void versions.refetch()} retryLabel={t("common.retry")} />;
  if (versions.isPending) return <Skeleton className="h-56 rounded-card" aria-hidden="true" />;
  const { items, hidden, limit } = versions.data;
  const hasLive = items.some((item) => item.live);
  const draftChanged = versions.data.has_unpublished_changes || dirty;
  return (
    <div className="mx-auto max-w-4xl space-y-4">
      <div>
        <h2 className="text-lg font-bold">{t(`${V}.title`)}</h2>
        <p className="text-sm text-fg-muted">{t(`${V}.subtitle`)}</p>
      </div>
      {hidden > 0 ? (
        <p className="flex flex-wrap items-center gap-2 rounded-md bg-info-soft p-3 text-xs" role="note" data-testid="versions-hidden">
          <Icon name="lock" className="size-4 shrink-0" />
          {t(`${V}.hidden`, { count: hidden, limit: limit ?? items.length })}
          <AppLink className="font-semibold text-primary-ink underline" href={consoleRoutes.billing(summary.slug)}>{t(`${V}.upgrade`)}</AppLink>
        </p>
      ) : null}
      {items.length === 0 ? <EmptyState icon="history" headingLevel={3} title={t(`${V}.emptyTitle`)} description={t(`${V}.emptyBody`)} /> : (
        <ul className="card divide-y divide-line">
          {draftChanged ? (
            <li className="flex flex-col gap-2 p-4 sm:flex-row sm:items-center sm:justify-between" data-testid="draft-row">
              <div>
                <p className="flex items-center gap-2 font-semibold">
                  {t(`${V}.draft`)}
                  <Badge tone="warning">{t("engage.workflows.unpublished")}</Badge>
                </p>
                <p className="text-xs text-fg-subtle">{t(`${V}.draftMeta`, { name: workflow.updated_by_name ?? "—", when: format.dateTime(workflow.updated_at) })}</p>
              </div>
              {hasLive ? <Button variant="secondary" size="sm" onClick={() => { setComparing({ base: DIFF_LIVE, target: DIFF_DRAFT }); }}>{t(`${V}.compareWithLive`)}</Button> : null}
            </li>
          ) : null}
          {items.map((version) => (
            <VersionRow
              key={version.id}
              version={version}
              canManage={canManage}
              onView={() => { setViewing(version.number); }}
              onCompare={() => { setComparing({ base: String(version.number), target: DIFF_LIVE }); }}
              onRollback={() => { requestRollback(version.number); }}
            />
          ))}
        </ul>
      )}
      {viewing !== null ? <VersionSheet workflowId={workflow.id} trigger={trigger} number={viewing} onClose={() => { setViewing(null); }} /> : null}
      {comparing ? (
        <VersionCompare
          workflowId={workflow.id}
          versions={items}
          base={comparing.base}
          target={comparing.target}
          unsaved={dirty}
          onChange={(base, target) => { setComparing({ base, target }); }}
          onClose={() => { setComparing(null); }}
        />
      ) : null}
      {confirming !== null ? <RollbackDialog number={confirming} unsaved={dirty} busy={rollbackMutation.isPending} onConfirm={() => { rollback(confirming, true); }} onClose={() => { setConfirming(null); }} /> : null}
    </div>
  );
}
